import { useState, useEffect } from 'react'
import { useOrgParams } from '~/common/use-org-params'
import { supabaseGetter } from '~/api/supabase-getter'

// Shoelace formula — area in square pixel units (0-1000 coordinate space)
const polygonPixelArea = (points) => {
  if (!points || points.length < 3) return 0
  let area = 0
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length
    area += points[i].x * points[j].y
    area -= points[j].x * points[i].y
  }
  return Math.abs(area / 2)
}

// Convert pixel-space area to m² using the farm's geographic bounds
const pixelAreaToM2 = (pixelArea, bounds) => {
  if (!bounds || bounds.length !== 4) return 0
  const [minLng, minLat, maxLng, maxLat] = bounds
  const avgLat = (minLat + maxLat) / 2
  const mPerDegLat = 111320
  const mPerDegLng = 111320 * Math.cos((avgLat * Math.PI) / 180)
  const widthM = (maxLng - minLng) * mPerDegLng
  const heightM = (maxLat - minLat) * mPerDegLat
  return pixelArea * (widthM / 1000) * (heightM / 1000)
}

export const usePlotAreaData = () => {
  const { farmId, plotId } = useOrgParams()
  const [areaM2, setAreaM2] = useState(null)
  const [plotName, setPlotName] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchArea() {
      if (!farmId) {
        setAreaM2(null)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // Fetch climate bounds for the farm
        const { data: climate } = await supabaseGetter.climates.getByOrganization(farmId)
        const bounds = climate?.bounds
        if (!bounds) { setAreaM2(null); return }

        // Fetch all plot orgs for this farm
        const { data: plotOrgs } = await supabaseGetter.organizations.getPlots(farmId)
        if (!plotOrgs?.length) { setAreaM2(null); return }

        // If a specific plot is active, only use that plot
        const targetPlots = plotId
          ? plotOrgs.filter(p => p.id === plotId)
          : plotOrgs

        if (!targetPlots.length) { setAreaM2(null); return }

        const plotIds = targetPlots.map(p => p.id)
        const { data: polygons } = await supabaseGetter.organizationPolygons.getByOrganizations(plotIds)
        if (!polygons?.length) { setAreaM2(null); return }

        const totalM2 = polygons.reduce((sum, poly) => {
          if (!poly.points || poly.points.length < 3) return sum
          return sum + pixelAreaToM2(polygonPixelArea(poly.points), bounds)
        }, 0)

        setAreaM2(totalM2 > 0 ? totalM2 : null)
        if (plotId && targetPlots.length === 1) setPlotName(targetPlots[0].name)
        else setPlotName(null)
      } catch {
        setAreaM2(null)
      } finally {
        setLoading(false)
      }
    }

    fetchArea()
  }, [farmId, plotId])

  return { areaM2, plotName, loading }
}
